<template>
  <transition name="modal">
    <div v-if="show" class="barcode-scanner-overlay" @click.self="close">
      <div class="barcode-scanner-modal" @click.stop>
        <div class="scanner-header">
          <h3 class="scanner-title">
            <i class="bi bi-upc-scan"></i>
            Scanner le code-barres
          </h3>
          <button class="btn-close" @click="close" aria-label="Fermer">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="scanner-body">
          <div v-if="processing" class="scanner-processing">
            <div class="spinner"></div>
            <p>Analyse de l'image en cours...</p>
            <div class="processing-animation">
              <div class="pulse-dot"></div>
              <div class="pulse-dot"></div>
              <div class="pulse-dot"></div>
            </div>
          </div>

          <div v-else-if="error" class="scanner-error">
            <i class="bi bi-exclamation-triangle"></i>
            <p>{{ error }}</p>
            <button class="btn btn-primary" @click="resetError">
              <i class="bi bi-arrow-clockwise"></i>
              Réessayer
            </button>
          </div>

          <div v-else-if="previewImage" class="camera-viewport">
            <div class="preview-container" ref="previewContainer">
              <img
                ref="previewImageElement"
                :src="previewImage"
                alt="Preview"
                class="preview-image"
                @load="onImageLoad"
              />
              <div class="scan-overlay">
                <div
                  class="scan-frame"
                  :style="barcodeFrameStyle"
                  v-if="barcodeDetected"
                >
                  <div class="scan-corner scan-corner-top-left"></div>
                  <div class="scan-corner scan-corner-top-right"></div>
                  <div class="scan-corner scan-corner-bottom-left"></div>
                  <div class="scan-corner scan-corner-bottom-right"></div>
                </div>
                <div class="scan-frame scan-frame-default" v-else>
                  <div class="scan-corner scan-corner-top-left"></div>
                  <div class="scan-corner scan-corner-top-right"></div>
                  <div class="scan-corner scan-corner-bottom-left"></div>
                  <div class="scan-corner scan-corner-bottom-right"></div>
                </div>
                <p class="scan-hint">
                  <i class="bi bi-upc-scan"></i>
                  <span v-if="barcodeDetected"> Code-barres détecté ! </span>
                  <span v-else-if="detecting"> Détection en cours... </span>
                  <span v-else>
                    Vérifiez que le code-barres est dans le cadre
                  </span>
                </p>
              </div>
            </div>
            <div class="camera-controls">
              <button class="btn btn-secondary" @click="resetPreview">
                <i class="bi bi-arrow-left"></i>
                Reprendre
              </button>
              <button
                class="btn btn-primary btn-capture"
                @click="scanPreviewImage"
                :disabled="processing || detecting"
              >
                <i class="bi bi-check-circle"></i>
                <span v-if="detecting">Détection...</span>
                <span v-else-if="barcodeDetected">Scanner</span>
                <span v-else>Analyser</span>
              </button>
            </div>
          </div>

          <div v-else class="scanner-options">
            <div class="scanner-instructions">
              <i class="bi bi-info-circle"></i>
              <p>Prenez une photo du code-barres ou choisissez une image</p>
            </div>
            <div class="scanner-buttons">
              <input
                ref="cameraInput"
                type="file"
                accept="image/*"
                capture="environment"
                @change="handleCameraCapture"
                style="display: none"
              />
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                @change="handleImageSelect"
                style="display: none"
              />
              <button class="btn btn-primary btn-scan" @click="openCamera">
                <i class="bi bi-camera"></i>
                <span>Prendre une photo</span>
              </button>
              <button
                class="btn btn-secondary btn-scan"
                @click="openFilePicker"
              >
                <i class="bi bi-folder2-open"></i>
                <span>Choisir une image</span>
              </button>
            </div>
          </div>
        </div>

        <div class="scanner-footer">
          <button class="btn btn-light" @click="close">
            <i class="bi bi-x-circle"></i>
            Annuler
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "BarcodeScannerModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    apiUrl: {
      type: String,
      default: "/api/items/read-barcode", // Default API endpoint for barcode scanning
    },
    useApi: {
      type: Boolean,
      default: false, // Set to true to use API instead of local scanning
    },
  },
  data() {
    return {
      processing: false,
      error: null,
      previewImage: null,
      selectedFile: null,
      detecting: false,
      barcodeDetected: false,
      barcodeLocation: null,
      imageDimensions: { width: 0, height: 0 },
    };
  },
  computed: {
    barcodeFrameStyle() {
      if (!this.barcodeLocation || !this.imageDimensions.width) {
        return {};
      }

      const { x, y, width, height } = this.barcodeLocation;
      const imgWidth = this.imageDimensions.width;
      const imgHeight = this.imageDimensions.height;

      // Calculate percentage positions
      const left = (x / imgWidth) * 100;
      const top = (y / imgHeight) * 100;
      const frameWidth = (width / imgWidth) * 100;
      const frameHeight = (height / imgHeight) * 100;

      return {
        left: `${left}%`,
        top: `${top}%`,
        width: `${frameWidth}%`,
        height: `${frameHeight}%`,
        position: "absolute",
      };
    },
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.reset();
      }
    },
  },
  methods: {
    openCamera() {
      this.$refs.cameraInput.click();
    },
    async handleCameraCapture(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        this.error = "Veuillez sélectionner une image.";
        return;
      }

      this.error = null;
      this.selectedFile = file;
      this.barcodeDetected = false;
      this.barcodeLocation = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);

      // Reset input
      event.target.value = "";
    },
    async onImageLoad() {
      await this.$nextTick();
      if (this.$refs.previewImageElement) {
        const img = this.$refs.previewImageElement;
        this.imageDimensions = {
          width: img.naturalWidth || img.clientWidth,
          height: img.naturalHeight || img.clientHeight,
        };
        // Don't auto-detect, let user click the button
      }
    },
    async detectBarcode() {
      if (!this.selectedFile || this.detecting) return;

      this.detecting = true;
      this.barcodeDetected = false;
      this.barcodeLocation = null;

      try {
        // Use local detection for preview (API detection happens on scan)
        // Dynamically import html5-qrcode
        const { Html5Qrcode } = await import("html5-qrcode");

        // Create scanner instance
        const scanner = new Html5Qrcode();

        try {
          // Scan file - html5-qrcode returns the decoded text
          const decodedText = await scanner.scanFile(this.selectedFile, true);

          if (decodedText) {
            this.barcodeDetected = true;

            // Since html5-qrcode doesn't provide exact coordinates,
            // we'll use image processing to find the barcode location
            await this.findBarcodeLocation();
          }
        } catch (scanError) {
          // No barcode detected - this is normal
          this.barcodeDetected = false;
          this.barcodeLocation = null;
        }
      } catch (error) {
        console.error("Barcode detection error:", error);
      } finally {
        this.detecting = false;
      }
    },
    async findBarcodeLocation() {
      // Create a canvas to analyze the image
      const img = this.$refs.previewImageElement;
      if (!img) return;

      await this.$nextTick();

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.naturalWidth || img.clientWidth;
      canvas.height = img.naturalHeight || img.clientHeight;
      ctx.drawImage(img, 0, 0);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Simple barcode detection: look for horizontal lines (dark/light patterns)
      // This is a simplified approach - for better results, use a dedicated barcode detection library
      const width = canvas.width;
      const height = canvas.height;
      const scanLines = Math.floor(height / 10); // Sample every 10th row

      let maxContrast = 0;
      let bestY = height / 2;
      let bestWidth = width * 0.6;
      let bestX = width * 0.2;

      // Scan for high contrast horizontal lines (typical of barcodes)
      for (
        let y = Math.floor(height * 0.2);
        y < Math.floor(height * 0.8);
        y += scanLines
      ) {
        let contrast = 0;
        let minBrightness = 255;
        let maxBrightness = 0;

        // Sample a horizontal line
        for (
          let x = Math.floor(width * 0.1);
          x < Math.floor(width * 0.9);
          x++
        ) {
          const idx = (y * width + x) * 4;
          const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
          minBrightness = Math.min(minBrightness, brightness);
          maxBrightness = Math.max(maxBrightness, brightness);
        }

        contrast = maxBrightness - minBrightness;

        if (contrast > maxContrast) {
          maxContrast = contrast;
          bestY = y;
        }
      }

      // Estimate barcode dimensions (typical barcode aspect ratio)
      const barcodeHeight = height * 0.15;
      const barcodeWidth = width * 0.6;
      const barcodeX = (width - barcodeWidth) / 2;
      const barcodeY = bestY - barcodeHeight / 2;

      // Ensure barcode location is within image bounds
      this.barcodeLocation = {
        x: Math.max(0, Math.min(barcodeX, width - barcodeWidth)),
        y: Math.max(0, Math.min(barcodeY, height - barcodeHeight)),
        width: Math.min(barcodeWidth, width),
        height: Math.min(barcodeHeight, height),
      };

      // Don't auto-scan, let user click the button
      // Auto-scan can be enabled by uncommenting below:
      // setTimeout(() => {
      //   if (this.barcodeDetected && !this.processing && !this.detecting) {
      //     this.scanPreviewImage();
      //   }
      // }, 1500);
    },
    resetPreview() {
      this.previewImage = null;
      this.selectedFile = null;
      this.barcodeDetected = false;
      this.barcodeLocation = null;
      this.detecting = false;
      this.imageDimensions = { width: 0, height: 0 };
    },
    async scanPreviewImage() {
      if (!this.selectedFile) {
        this.error = "Aucune image à analyser.";
        return;
      }

      // Reset detection state
      this.detecting = false;
      this.barcodeDetected = false;
      this.error = null;

      // Scan the image
      await this.scanImageFile(this.selectedFile);
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    async handleImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        this.error = "Veuillez sélectionner une image.";
        return;
      }

      this.error = null;
      this.selectedFile = file;
      this.barcodeDetected = false;
      this.barcodeLocation = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);

      // Reset input
      event.target.value = "";
    },
    async scanImageFile(file) {
      this.error = null;
      this.processing = true;

      // Create preview if not already set
      if (!this.previewImage) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewImage = e.target.result;
        };
        reader.readAsDataURL(file);
      }

      try {
        console.log(
          "scanImageFile called, useApi:",
          this.useApi,
          "apiUrl:",
          this.apiUrl
        );

        // Use API if configured, otherwise use local scanning
        if (this.useApi && this.apiUrl) {
          console.log("Using API for scanning");
          await this.scanWithAPI(file);
        } else {
          console.log("Using local scanning");
          await this.scanLocally(file);
        }
      } catch (error) {
        console.error("Barcode scanner error:", error);
        // Don't override error message if it was already set
        if (!this.error) {
          this.error = "Erreur lors du scan. Veuillez réessayer.";
        }
      } finally {
        this.processing = false;
      }
    },
    async scanWithAPI(file) {
      try {
        // Create FormData to send image
        const formData = new FormData();
        formData.append("image", file);
        // You can add other parameters if needed
        // formData.append("format", "barcode");

        // Use the provided API URL or default
        const apiEndpoint = this.apiUrl || "/api/items/read-barcode";

        // Send image to API
        // Note: Content-Type will be set automatically by axios for FormData with boundary
        // Authorization header is automatically added by axios interceptor
        console.log("Sending image to API:", apiEndpoint);
        console.log("File details:", {
          name: file.name,
          type: file.type,
          size: file.size,
        });

        const response = await this.$api.instance.post(apiEndpoint, formData, {
          timeout: 30000, // 30 seconds timeout for image processing
        });

        console.log("API response:", response);
        console.log("API response data:", response.data);

        // Extract barcode from response
        // API returns: { status: "success", message: "...", barcode_text: "..." }
        let decodedText = null;

        if (response.data) {
          // Try different possible response structures
          if (response.data.barcode_text) {
            decodedText = response.data.barcode_text;
          } else if (response.data.barcode) {
            decodedText = response.data.barcode;
          } else if (response.data.code) {
            decodedText = response.data.code;
          } else if (response.data.data?.barcode_text) {
            decodedText = response.data.data.barcode_text;
          } else if (response.data.data?.barcode) {
            decodedText = response.data.data.barcode;
          } else if (response.data.data?.code) {
            decodedText = response.data.data.code;
          } else if (typeof response.data === "string") {
            decodedText = response.data;
          } else if (response.data.result) {
            decodedText = response.data.result;
          }
        }

        // Check if API returned success status
        if (
          response.data &&
          response.data.status === "success" &&
          decodedText
        ) {
          // Success - emit the scanned barcode first
          console.log("Barcode scanned successfully:", decodedText);
          this.$emit("scanned", decodedText);

          // Show success notification
          this.$store.dispatch("notification/store", {
            status: "success",
            message: response.data.message || "Code-barres scanné avec succès.",
          });

          // Reset processing state
          this.processing = false;

          // Wait a moment to show success, then close modal
          await this.$nextTick();
          setTimeout(() => {
            console.log("Closing modal after success...");
            // Emit close event directly to ensure modal closes
            this.$emit("close");
            // Also reset state
            this.reset();
          }, 500);
        } else {
          throw new Error(
            response.data?.message || "Aucun code-barres détecté dans l'image."
          );
        }
      } catch (error) {
        console.error("API scan error:", error);
        if (error.response) {
          // API returned an error response
          const errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            `Erreur API: ${error.response.status}`;
          this.error = errorMessage;
        } else if (error.request) {
          // Request was made but no response received
          this.error =
            "Impossible de contacter le serveur. Vérifiez votre connexion.";
        } else {
          // Something else happened
          this.error =
            error.message || "Erreur lors de l'envoi de l'image à l'API.";
        }
        throw error;
      }
    },
    async scanLocally(file) {
      try {
        console.log(
          "Starting local scan with file:",
          file.name,
          file.type,
          file.size
        );

        // Dynamically import html5-qrcode
        const { Html5Qrcode } = await import("html5-qrcode");
        console.log("Html5Qrcode imported successfully");

        // Scan the image file
        const scanner = new Html5Qrcode();

        try {
          console.log("Scanning file...");
          // Try scanning - html5-qrcode supports both QR codes and barcodes
          // The second parameter (true) enables verbose logging
          let decodedText = null;

          // Try scanning with file directly first
          try {
            decodedText = await scanner.scanFile(file, true);
          } catch (err) {
            console.log("File scan failed, trying with data URL...", err);
            // If scanFile fails with file, try with data URL
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            decodedText = await scanner.scanFile(dataUrl, true);
          }

          console.log("Scan result:", decodedText);

          if (decodedText && decodedText.trim()) {
            // Success - emit the scanned barcode
            console.log("Barcode detected:", decodedText);
            this.$emit("scanned", decodedText.trim());
            this.$store.dispatch("notification/store", {
              status: "success",
              message: "Code-barres scanné avec succès.",
            });
            this.close();
          } else {
            throw new Error("Aucun code-barres détecté dans l'image.");
          }
        } catch (scanError) {
          console.error("Scan error details:", scanError);
          console.error("Error name:", scanError.name);
          console.error("Error message:", scanError.message);
          console.error("Error stack:", scanError.stack);

          // More specific error messages
          if (scanError.message) {
            const errorMsg = scanError.message.toLowerCase();
            if (
              errorMsg.includes("no qr code") ||
              errorMsg.includes("not found")
            ) {
              this.error =
                "Aucun code-barres détecté dans l'image. Assurez-vous que le code-barres est clair et bien visible.";
            } else if (
              errorMsg.includes("file") ||
              errorMsg.includes("format")
            ) {
              this.error =
                "Format d'image non supporté. Veuillez utiliser une image JPG ou PNG.";
            } else if (
              errorMsg.includes("permission") ||
              errorMsg.includes("access")
            ) {
              this.error = "Erreur d'accès au fichier. Veuillez réessayer.";
            } else {
              this.error =
                scanError.message ||
                "Impossible de lire le code-barres. Veuillez réessayer avec une image plus claire.";
            }
          } else {
            this.error =
              "Impossible de lire le code-barres. Veuillez réessayer avec une image plus claire.";
          }
          throw scanError;
        }
      } catch (error) {
        console.error("Local scan error:", error);
        // If error message wasn't set above, set a generic one
        if (!this.error) {
          this.error =
            "Erreur lors du scan. Veuillez vérifier que l'image contient un code-barres valide.";
        }
        throw error;
      }
    },
    resetError() {
      this.error = null;
      this.previewImage = null;
      this.selectedFile = null;
    },
    reset() {
      this.processing = false;
      this.error = null;
      this.previewImage = null;
      this.selectedFile = null;
      this.barcodeDetected = false;
      this.barcodeLocation = null;
      this.detecting = false;
      this.imageDimensions = { width: 0, height: 0 };
      if (this.$refs.cameraInput) {
        this.$refs.cameraInput.value = "";
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    close() {
      console.log("Closing modal...");
      this.reset();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.barcode-scanner-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.barcode-scanner-modal {
  width: 100%;
  max-width: 500px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.scanner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.scanner-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.scanner-title i {
  color: #3b82f6;
  font-size: 1.5rem;
}

.btn-close {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #e2e8f0;
  color: #475569;
}

.scanner-body {
  padding: 0;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.camera-viewport {
  width: 100%;
  position: relative;
  background: #000;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.preview-container {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.scan-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scan-frame {
  aspect-ratio: 2.5 / 1;
  position: absolute;
  border: 2px solid rgba(59, 130, 246, 0.8);
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  transition: all 0.3s ease;
  animation: pulse 2s ease-in-out infinite;
}

.scan-frame-default {
  width: 80%;
  max-width: 300px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@keyframes pulse {
  0%,
  100% {
    border-color: rgba(59, 130, 246, 0.8);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
  }
  50% {
    border-color: rgba(16, 185, 129, 0.9);
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
}

.scan-corner {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid #3b82f6;
}

.scan-corner-top-left {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.scan-corner-top-right {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
}

.scan-corner-bottom-left {
  bottom: -3px;
  left: -3px;
  border-right: none;
  border-top: none;
}

.scan-corner-bottom-right {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
}

.scan-hint {
  margin-top: 2rem;
  padding: 0.75rem 1.5rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 20px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: auto;
}

.scan-hint i {
  font-size: 1.2rem;
}

.camera-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn-capture {
  min-width: 120px;
  font-size: 1rem;
  padding: 1rem 2rem;
}

.scanner-options {
  width: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
}

.scanner-instructions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
  color: #64748b;
}

.scanner-instructions i {
  font-size: 2.5rem;
  color: #3b82f6;
}

.scanner-instructions p {
  margin: 0;
  font-size: 0.95rem;
}

.scanner-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.btn-scan {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  font-size: 0.95rem;
}

.preview-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.preview-label {
  margin: 0;
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.preview-wrapper {
  width: 100%;
  max-width: 300px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #3b82f6;
  background: #f1f5f9;
}

.preview-wrapper img {
  width: 100%;
  height: auto;
  display: block;
}

.scanner-processing,
.scanner-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
  padding: 2rem;
  width: 100%;
}

.processing-animation {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.pulse-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3b82f6;
  animation: pulse-dot 1.4s ease-in-out infinite;
}

.pulse-dot:nth-child(1) {
  animation-delay: 0s;
}

.pulse-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.pulse-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse-dot {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

.scanner-error i {
  font-size: 3rem;
  color: #ef4444;
}

.scanner-error p {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.scanner-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-light:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-secondary {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .barcode-scanner-modal {
    max-width: 100%;
    border-radius: 0;
    max-height: 100vh;
  }

  .scanner-body {
    min-height: 300px;
  }
}
</style>
